/**
 * ToolCallsContainer Component
 *
 * Container for tool call cards.
 * During streaming: shows all cards expanded.
 * After completion: shows collapsible summary bar.
 */

import { Box, Typography, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState } from 'react';
import { ToolCall } from '../../contexts/chat/type';
import { ToolCallCard } from './tool_call_card';

interface ToolCallsContainerProps {
  toolCalls: ToolCall[];
  isStreaming: boolean;
}

export const ToolCallsContainer = ({ toolCalls, isStreaming }: ToolCallsContainerProps) => {
  const [expanded, setExpanded] = useState(true);

  if (toolCalls.length === 0) return null;

  // Calculate summary stats
  const successCount = toolCalls.filter(t => t.status === 'success').length;
  const errorCount = toolCalls.filter(t => t.status === 'error').length;
  const totalDuration = toolCalls.reduce((sum, t) => sum + (t.duration ?? 0), 0);
  const runTimestamp = toolCalls[0]?.metadata.runTimestamp;
  const runId = toolCalls[0]?.metadata.runId;

  // Format run ID for display (last 4 chars)
  const displayRunId = runId ? runId.slice(-4) : null;

  // While streaming: show all cards expanded
  if (isStreaming) {
    return (
      <Box sx={{ mb: 2 }}>
        {toolCalls.map((toolCall) => (
          <ToolCallCard
            key={toolCall.id}
            toolCall={toolCall}
            isStreaming={true}
            defaultExpanded={true}
          />
        ))}
      </Box>
    );
  }

  // After completion: collapsible summary
  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        mb: 2,
        overflow: 'hidden',
      }}
    >
      {/* Summary Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#f1f3f4' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {displayRunId && runTimestamp && (
            <>
              <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                {'\uD83D\uDCCA'} Run #{displayRunId}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>{'\u00B7'}</Typography>
            </>
          )}
          <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
            {successCount} tool{successCount !== 1 ? 's' : ''}
            {errorCount > 0 && (
              <span style={{ color: '#ef4444' }}> ({errorCount} failed)</span>
            )}
          </Typography>
          {totalDuration > 0 && (
            <>
              <Typography sx={{ fontSize: '12px', color: '#9ca3af' }}>{'\u00B7'}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                {(totalDuration / 1000).toFixed(1)}s
              </Typography>
            </>
          )}
        </Box>
        <IconButton size="small" sx={{ p: 0.5 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expanded Tool Cards */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {toolCalls.map((toolCall) => (
            <ToolCallCard
              key={toolCall.id}
              toolCall={toolCall}
              isStreaming={false}
              defaultExpanded={false}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
